import { useCallback, useEffect, useRef } from 'react'
import { useNavigationBlocker } from '../../../../contexts/navigation-blocker-context'
import { confirmDiscardChanges } from '../utils/confirm-discard-changes'

interface UseUnsavedChangesNavigationGuardOptions {
  enabled: boolean
  hasUnsavedChanges: boolean
  hasUnsavedChangesRef: React.MutableRefObject<boolean>
  onDiscard: () => void
}

export const useUnsavedChangesNavigationGuard = ({
  enabled,
  hasUnsavedChanges,
  hasUnsavedChangesRef,
  onDiscard,
}: UseUnsavedChangesNavigationGuardOptions) => {
  const onDiscardRef = useRef(onDiscard)
  onDiscardRef.current = onDiscard

  const confirmLeave = useCallback(async (): Promise<boolean> => {
    if (!hasUnsavedChangesRef.current) {
      return true
    }

    const confirmed = await confirmDiscardChanges()
    if (confirmed) {
      hasUnsavedChangesRef.current = false
      onDiscardRef.current()
    }

    return confirmed
  }, [hasUnsavedChangesRef])

  useNavigationBlocker(enabled, confirmLeave)

  useEffect(() => {
    if (!enabled || !hasUnsavedChanges) {
      return
    }

    const url = window.location.href
    window.history.pushState({ employeeFormGuard: true }, '', url)

    const handlePopState = () => {
      window.history.pushState({ employeeFormGuard: true }, '', url)

      void confirmLeave().then(confirmed => {
        if (!confirmed) {
          return
        }

        window.removeEventListener('popstate', handlePopState)
        window.history.go(-2)
      })
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [enabled, hasUnsavedChanges, confirmLeave])
}
